import React from 'react'
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Axios from 'axios';
import Alert from '../../../components/alert/Alert';
import { tokens } from "../../../theme";
import { useTheme, Typography } from "@mui/material";
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import IconButton from '@mui/material/IconButton';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import { getAuthHeader } from '../../../utils/authToken';

export default function UploadResumeTab({ 
    teacher, 
    setTeacherUpdated,
    socket = null,
    socketConnected = false,
    from_weekly = false,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

    const [resume, setResume] = React.useState(null);
    const [isDragging, setIsDragging] = React.useState(false);

    // Handle drag and drop
    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            setResume(files[0]);
        }
    };

    const removeResume = async () => {
        try {
            const response = await Axios.delete(`${process.env.REACT_APP_URL}/api/v1/teachers/${teacher.id}/resumes/${teacher.resumes[0].id}`, {
                headers: {
                    Authorization: getAuthHeader(),
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 200) {
                setTeacherUpdated(true);
                setSuccessAlertMessage("Resume removed successfully.");
                setSuccessAlertOpen(true);
            } else {
                setErrorAlertMessage("Failed to remove resume.");
                setErrorAlertOpen(true);
            }
        } catch (error) {
            console.error("Error removing resume:", error);
            setErrorAlertMessage("An error occurred while removing the resume.");
            setErrorAlertOpen(true);
        }
    }

    const uploadResume = async () => {
        if (resume === null) {
            setErrorAlertMessage("Please select a resume to upload.");
            setErrorAlertOpen(true);
            return;
        }

        const formData = new FormData();
        formData.append('resume', resume);

        try {            
            await Axios.post(`${process.env.REACT_APP_URL}/api/v1/teachers/${teacher.id}/resumes`, 
                formData, {
                    headers: {
                        Authorization: getAuthHeader(),
                        'Content-Type': 'multipart/form-data',
                    },
                }
            ).then((response) => {
                setTeacherUpdated(true);
                setSuccessAlertMessage("Resume uploaded successfully.");
                setSuccessAlertOpen(true);
            }).catch((error) => {
                console.log(error.response?.data?.message || error.response?.data?.error || "Error uploading resume");
                setErrorAlertOpen(true);
                setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Error uploading resume");
            });
        } catch (error) {
            console.error("Error uploading resume:", error);
            setErrorAlertMessage("An error occurred while uploading the resume.");
            setErrorAlertOpen(true);
        }
    }

    return (
        <>
            <Box sx={{ width: '100%' }}>
                {teacher.resumes.length > 0 ? (
                    <>
                        <Grid container direction="row" alignItems="center" ml={3}>
                            <Grid item>
                                <AssignmentIndIcon sx={{ marginTop: "5px", }} />
                            </Grid>
                            <Grid item>
                                <Typography component='a' mb={1} ml={1} variant='h5' href={teacher.resumes[0].file_url} target='_blank'>
                                    {teacher.resumes[0].name}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => {
                                    if (teacher.resumes.length === 0) {
                                        setErrorAlertMessage("No resume to remove.");
                                        setErrorAlertOpen(true);
                                        return;
                                    }
                                    removeResume();
                                }}
                            >
                                Remove Resume
                            </Button>
                        </Box>
                    </>
                ) : (
                    <>
                        <Typography component='p' ml={3} variant='h5'>
                            No resume uploaded for this teacher.
                        </Typography>

                        <Box
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById("resumeUploadInput").click()}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            gap={2}
                            mt={2}
                            sx={{
                                border: isDragging ? "2px dashed #4CAF50" : "2px dashed #aaa",
                                backgroundColor: isDragging ? "#e8f5e9" : "#f5f5f5",
                                padding: 3,
                                borderRadius: 2,
                                textAlign: "center",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    backgroundColor: "#e0e0e0",
                                    borderColor: colors.primary.main
                                }
                            }}
                        >
                            <FileUploadOutlined fontSize="large" sx={{ color: colors.primary.main }} />
                            <Typography variant="body1" fontWeight="bold">
                                Click to upload or drag and drop
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                PDF, DOC, DOCX files supported
                            </Typography>

                            {/* Hidden File Input */}
                            <input
                                id="resumeUploadInput"
                                multiple={false}
                                type="file"
                                hidden
                                onChange={(e) => setResume(e.target.files[0])}
                                name="[licenseFile]"
                            />
                        </Box>

                        {/* Selected File Display */}
                        {resume !== null && (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                mt={2}
                                sx={{
                                    padding: 2,
                                    borderRadius: 2,
                                    backgroundColor: colors.primary[700],
                                    boxShadow: 1,
                                }}
                            >
                                <Typography variant="body1" color={colors.primary[450]} sx={{ flex: 1 }}>
                                    {resume.name}
                                </Typography>
                                <IconButton onClick={() => setResume(null)} size="small">
                                    <ClearIcon sx={{ color: "red" }} />
                                </IconButton>
                            </Box>
                        )}

                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    if (resume === null) {
                                        setErrorAlertMessage("Please select a resume to upload.");
                                        setErrorAlertOpen(true);
                                        return;
                                    }

                                    uploadResume();
                                }}
                            >
                                Upload Resume
                            </Button>
                        </Box>
                    </>
                )}
            </Box>

            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical='top'
                horizontal='right'
            />
        </>
    )
}